import React, { useState } from "react";
import { ConfirmModal } from "../../../components/ui";

/**
 * SendToOwnerModal Component
 *
 * Modal for sending RFI to owner for response.
 * Collects owner email and optional CC emails.
 * Form values are retained when the modal is closed (via retainDataOnClose; cleared on page leave/refresh).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Function} props.onConfirm - Callback to confirm send (receives { owner_email, cc_emails })
 * @param {boolean} props.isSubmitting - Whether submission is in progress
 * @param {Object} props.rfi - RFI instance
 * @returns {JSX.Element}
 */
const SendToOwnerModal = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  rfi,
}) => {
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [emailError, setEmailError] = useState("");

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * Validate optional comma-separated CC emails. Empty is valid.
   *
   * @param {string} emailString Raw CC field value.
   * @returns {boolean}
   */
  const validateMultipleEmails = (emailString) => {
    if (!emailString || emailString.trim() === "") {
      return true; // Empty is valid for optional field
    }

    const emails = emailString.split(",").map((e) => e.trim());
    return emails.every((email) => emailRegex.test(email));
  };

  /**
   * Validate owner/CC emails and call `onConfirm` with `{ owner_email, cc_emails }`.
   *
   * @returns {void}
   */
  const handleConfirm = () => {
    // Validate owner email
    if (!ownerEmail) {
      setEmailError("Owner email is required");
      return;
    }

    if (!emailRegex.test(ownerEmail)) {
      setEmailError("Please enter a valid owner email address");
      return;
    }

    // Validate CC emails if provided (supports multiple comma-separated emails)
    if (ccEmails && !validateMultipleEmails(ccEmails)) {
      setEmailError(
        "Please enter valid CC email address(es). Multiple emails should be separated by commas."
      );
      return;
    }

    // Clear errors and proceed
    setEmailError("");
    onConfirm({
      owner_email: ownerEmail,
      cc_emails: ccEmails || null,
    });
  };

  /**
   * Clear the inline email error and close. Form values are retained
   * (`retainDataOnClose`).
   *
   * @returns {void}
   */
  const handleClose = () => {
    setEmailError("");
    onClose();
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title="Send RFI to Owner"
      buttonText={isSubmitting ? "Sending..." : "Save & Send"}
      isSubmitting={isSubmitting}
      retainDataOnClose
    >
      <div className="space-y-4">
        <p className="text-gray-700">
          This will save the RFI and send an email to the owner with the RFI details and PDF attachment, requesting a response.
        </p>

        {/* Owner Email Input */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Owner Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={ownerEmail}
            onChange={(e) => {
              setOwnerEmail(e.target.value);
              setEmailError("");
            }}
            placeholder="owner@example.com"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            autoFocus
          />
        </div>

        {/* CC Emails Input (Optional) */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            CC Emails (Optional)
          </label>
          <input
            type="text"
            value={ccEmails}
            onChange={(e) => {
              setCcEmails(e.target.value);
              setEmailError("");
            }}
            placeholder="email1@example.com, email2@example.com"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Send a copy to multiple recipients (separate emails with commas)
          </p>
        </div>

        {/* Error Message */}
        {emailError && (
          <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-700">
            {emailError}
          </div>
        )}
      </div>
    </ConfirmModal>
  );
};

export default SendToOwnerModal;
